import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import { listingApiFetchOwnRsvp } from 'modules/listing/events/api/listingApiFetchOwnRsvp';
import { listingApiPostEventRsvp } from 'modules/listing/events/api/listingApiPostEventRsvp';
import { userDashboardApiEnableProfileFeatures } from 'modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import { showModal } from 'zustand-stores/modalStore';
import {
  setPreSignupRsvpEventId,
  usePreSignupRsvp,
} from 'zustand-stores/preSignupRsvpStore';

export type RsvpAnswerContextType = {
  answer: string | null;
  setAnswer: null | ((ans: string | null) => void);
};

export const RsvpAnswerContext = createContext<RsvpAnswerContextType>({
  answer: null,
  setAnswer: () => {},
});

// NOTE exactly one use of this hook must have doInit set
export function useRsvpAnswer(listingId: string, doInit = false) {
  const { i18n } = useI18n();
  const { answer, setAnswer } = useContext(RsvpAnswerContext);

  const { user } = useUser();
  const { preSignupRsvpEventId, preSignupRsvpAnswer } = usePreSignupRsvp();
  const [wasUnauthenticated, setWasUnauthenticated] = useState(false);

  useEffect(() => {
    if (
      doInit &&
      user &&
      (!preSignupRsvpEventId || preSignupRsvpEventId !== listingId)
    ) {
      listingApiFetchOwnRsvp(listingId).then((r) => {
        if (r?.answer && setAnswer) {
          setAnswer(r.answer);
        }
      });
    }
  }, [
    doInit,
    listingId,
    preSignupRsvpAnswer,
    preSignupRsvpEventId,
    setAnswer,
    user,
  ]);

  const handlePostRsvp = useCallback(
    (ans: string) => {
      listingApiPostEventRsvp(listingId, {
        answer: ans,
      }).then(() => {
        if (setAnswer) {
          setAnswer(ans);
        }
      });
    },
    [listingId, setAnswer],
  );

  const handleRsvpNoProfile = useCallback(
    (ans: string) =>
      userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {
          onUserProfileCreated: () => {
            handlePostRsvp(ans);
          },
        });
      }),
    [handlePostRsvp],
  );

  const handleRsvpUnauthenticated = useCallback(
    (ans: string) => {
      setWasUnauthenticated(true);
      setPreSignupRsvpEventId(listingId, ans);

      showModal('EMAIL_SIGNUP', {
        title: i18n('Sign up to RSVP to this event'),
        onLogin: (userArg) => {
          setPreSignupRsvpEventId(null, null);
          setWasUnauthenticated(false);
          if (!userArg.hasPublishedIdealistProfile) {
            handleRsvpNoProfile(ans);
          } else {
            handlePostRsvp(ans);
          }
        },
        requirePassword: true,
      });
    },
    [
      i18n,
      setWasUnauthenticated,
      listingId,
      handlePostRsvp,
      handleRsvpNoProfile,
    ],
  );

  const postRsvp = useCallback(
    (ans: string, u?: ApiUser) => {
      const userArg = u ?? user;
      if (!userArg) {
        return handleRsvpUnauthenticated(ans);
      }

      if (!userArg.hasPublishedIdealistProfile) {
        return handleRsvpNoProfile(ans);
      }

      handlePostRsvp(ans);
    },
    [handlePostRsvp, handleRsvpUnauthenticated, handleRsvpNoProfile, user],
  );

  useEffect(() => {
    if (
      user &&
      preSignupRsvpEventId &&
      preSignupRsvpAnswer &&
      preSignupRsvpEventId === listingId &&
      !wasUnauthenticated &&
      doInit &&
      user.emailVerified &&
      user.hasPublishedIdealistProfile
    ) {
      setPreSignupRsvpEventId(null, null);
      postRsvp(preSignupRsvpAnswer, user);
    }
  }, [
    user,
    preSignupRsvpEventId,
    preSignupRsvpAnswer,
    listingId,
    postRsvp,
    wasUnauthenticated,
    doInit,
    setAnswer,
  ]);

  return {
    answer,
    setAnswer,
    postRsvp,
  };
}
